// A workspace's own AI keys, whose AI runs for it, and the operator's "AI and SMS on us".
import { z } from "zod";
import { IsoDateTime, Uuid } from "./common";

export const AI_KEY_PROVIDERS = ["anthropic", "google", "openai"] as const;
export type AiKeyProvider = (typeof AI_KEY_PROVIDERS)[number];

/** `unverified`: the test call could not reach the provider, so nothing is known yet; the key is still tried. */
export const AI_KEY_STATUSES = ["ok", "invalid", "no_credit", "unverified"] as const;
export type AiKeyStatus = (typeof AI_KEY_STATUSES)[number];

/** On a paid plan: the plan's AI, or the workspace's own key. */
export const AI_KEY_CHOICES = ["included", "own"] as const;
export type AiKeyChoice = (typeof AI_KEY_CHOICES)[number];

/** Why the AI runs where it runs. */
export const AI_ROUTE_REASONS = ["host", "paid_included", "paid_own", "free", "trial", "grant", "unpaid"] as const;
export type AiRouteReason = (typeof AI_ROUTE_REASONS)[number];

/** What stops AI work on the workspace's own keys. Each is also the error code of the refused call. */
export const AI_KEY_BLOCKS = ["ai_key_required", "ai_key_invalid", "ai_key_no_credit"] as const;
export type AiKeyBlock = (typeof AI_KEY_BLOCKS)[number];

export const AiKeyDto = z.object({
  provider: z.enum(AI_KEY_PROVIDERS),
  /** The only part of the key that ever leaves the service. */
  last4: z.string(),
  status: z.enum(AI_KEY_STATUSES),
  checkedAt: IsoDateTime.nullable(),
  /** A stable code the UI translates, such as `http_401` or `google_free_tier`. */
  lastErrorCode: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AiKeyDto = z.infer<typeof AiKeyDto>;

export const AiKeySaveRequest = z
  .object({
    provider: z.enum(AI_KEY_PROVIDERS),
    key: z.string().trim().min(8).max(400),
  })
  .strict();
export type AiKeySaveRequest = z.infer<typeof AiKeySaveRequest>;

export const AiKeyChoicePut = z.object({ choice: z.enum(AI_KEY_CHOICES) }).strict();
export type AiKeyChoicePut = z.infer<typeof AiKeyChoicePut>;

const CapabilityProvider = z.enum(AI_KEY_PROVIDERS).nullable();

export const AiKeysStatusDto = z.object({
  source: z.enum(["service", "tenant_key"]),
  reason: z.enum(AI_ROUTE_REASONS),
  choice: z.enum(AI_KEY_CHOICES),
  /** Only on a paid plan may the owner choose between the plan's AI and the workspace's key. */
  canChoose: z.boolean(),
  keys: z.array(AiKeyDto),
  /** On the workspace's keys: which key writes, draws and edits pictures. Null where none can. On the service's AI, all null. */
  capabilities: z.object({ text: CapabilityProvider, images: CapabilityProvider, imageEdit: CapabilityProvider }),
  /** Set when AI work is stopped, with the reason the app shows. */
  blocked: z.enum(AI_KEY_BLOCKS).nullable(),
  /** The operator's "AI and SMS on us", when it applies. */
  grant: z.object({ monthlyCapUsd: z.number() }).nullable(),
});
export type AiKeysStatusDto = z.infer<typeof AiKeysStatusDto>;

export const AdminAiGrantDto = z.object({
  tenantId: Uuid,
  granted: z.boolean(),
  monthlyCapUsd: z.number().nullable(),
  reason: z.string().nullable(),
  grantedBy: z.string().nullable(),
  grantedAt: IsoDateTime.nullable(),
  /** What the service paid for this workspace's AI this month, in the workspace's own month. */
  spentUsd: z.number(),
  month: z.string(),
});
export type AdminAiGrantDto = z.infer<typeof AdminAiGrantDto>;

/** `monthlyCapUsd: null` takes the grant back. */
export const AdminAiGrantPut = z
  .object({
    monthlyCapUsd: z.number().positive().max(1000).nullable(),
    reason: z.string().trim().min(3).max(300),
  })
  .strict();
export type AdminAiGrantPut = z.infer<typeof AdminAiGrantPut>;
